import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Menu, MessageCircle, RefreshCw, Home, X, Bell } from 'lucide-react';
import Sidebar from './Sidebar';
import DashboardOverview from './DashboardOverview';
import DeclarationsTable from './DeclarationsTable';
import AllTransactionsTable from './AllTransactionsTable';
import RevenueAnalytics from './RevenueAnalytics';
import UserManagement from './UserManagement';
import Settings from './Settings';
import KYCManagement from './KYCManagement';
import SelfRegisteredAgents from './SelfRegisteredAgents';
import AgentPasswordManagement from './AgentPasswordManagement';
import CreateAgentsFromTQ from './CreateAgentsFromTQ';
import PaidNoCertAttempt from './PaidNoCertAttempt';
import SuperAdminManagement from './SuperAdminManagement';
import CommissionsPayable from './CommissionsPayable';
import UnderwritingIssueCover from './UnderwritingIssueCover';
import UnderwritingCoversIssued from './UnderwritingCoversIssued';
import AdminUnderwritingIssuedCovers from './AdminUnderwritingIssuedCovers';
import AdminComprehensiveIssued from './AdminComprehensiveIssued';
import AgentsAnalytics from './AgentsAnalytics';
import ProductRatesPage from './ProductRatesPage';
import UnsynchronizedPolicies from './UnsynchronizedPolicies';
import CoreRetryBackground from './CoreRetryBackground';
import AdminReports from './AdminReports';
import UnderwritingOverview from './UnderwritingOverview';
import CompContinuations from './agent/CompContinuations';
import Renewals from './Renewals';
import Snackbar from './Snackbar';
import MonarchLogo from './analytics/MonarchLogo';
import { getAdminRoleLabel } from '../utils/adminRoleLabel';
import { isFinanceDashboardRole, isUnderwritingRole, isCreditControlRole, staffRole } from '../utils/staffRole';
import { canSeeComprehensiveUnderwritingNav } from '../utils/comprehensiveAccess';
import { canSeeRenewals } from '../utils/renewalAccess';
import { getApiBaseUrl, getApiUrl } from '../config/api';
import './Sidebar.css'; // Ensure Sidebar styles are loaded
import './AdminDashboard.css';
import './AdminResponsive.css';
import './../styles/analytics-theme.css';

const COMP_EXT_TOAST_SESSION_KEY = 'comp_extend_toast_seen_v1';
const RENEWAL_TOAST_SESSION_KEY = 'tpo_tor_renewal_toast_seen_v1';

const AdminDashboard = ({ user: propUser, onLogout, onBackToMain }) => {
  const [user, setUser] = useState(propUser || { username: 'admin', role: 'admin' });
  const [activeTab, setActiveTab] = useState(
    isUnderwritingRole(propUser)
      ? 'underwriting-issue-cover'
      : (isCreditControlRole(propUser) ? 'self-registered-agents' : 'overview')
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  const [snackbarDuration, setSnackbarDuration] = useState(5000);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [compExtBadge, setCompExtBadge] = useState(0);
  const [compExtUrgent, setCompExtUrgent] = useState(false);
  const [compExtPreview, setCompExtPreview] = useState([]);
  const [compExtBellOpen, setCompExtBellOpen] = useState(false);
  const [compExtBellPos, setCompExtBellPos] = useState({ top: 0, right: 0 });
  const compExtBellRef = useRef(null);
  const compExtDropdownRef = useRef(null);
  const [renewalBadge, setRenewalBadge] = useState(0);
  const [renewalPreview, setRenewalPreview] = useState([]);
  const [renewalBellOpen, setRenewalBellOpen] = useState(false);
  const [renewalBellPos, setRenewalBellPos] = useState({ top: 0, right: 0 });
  const renewalBellRef = useRef(null);
  const renewalDropdownRef = useRef(null);
  const API_BASE_URL = getApiBaseUrl();
  const [dashboardData, setDashboardData] = useState({
    totalPolicies: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    activePolicies: 0,
    recentPolicies: []
  });

  const [snackbarNonce, setSnackbarNonce] = useState(0);

  const showNotification = (message, type = 'info', duration = 5000) => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarDuration(duration);
    setSnackbarNonce((n) => n + 1);
    setShowSnackbar(true);
  };

  // Underwriting only: COMP extension bell + toast (not super_admin)
  useEffect(() => {
    if (!isUnderwritingRole(user)) {
      setCompExtBadge(0);
      setCompExtUrgent(false);
      setCompExtPreview([]);
      return undefined;
    }

    let cancelled = false;
    const loadCompExtNotifications = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) return;

        const summaryRes = await fetch(getApiUrl('/api/admin/comp-continuations/summary'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const summary = await summaryRes.json().catch(() => ({}));
        if (cancelled) return;

        const actionNeeded = summaryRes.ok && summary.success ? Number(summary.actionNeeded) || 0 : 0;
        const badge = summaryRes.ok && summary.success ? Number(summary.badge) || actionNeeded : 0;
        const urgent = actionNeeded > 0;
        setCompExtBadge(badge);
        setCompExtUrgent(urgent);

        const listRes = await fetch(
          `${getApiUrl('/api/admin/comp-continuations')}?scope=action`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = await listRes.json().catch(() => ({}));
        if (cancelled) return;

        const items = listRes.ok && list.success && Array.isArray(list.items) ? list.items.slice(0, 5) : [];
        setCompExtPreview(items);

        // Toast is for underwriters who action extensions — not super_admin
        if (
          isUnderwritingRole(user) &&
          urgent &&
          badge > 0 &&
          !sessionStorage.getItem(COMP_EXT_TOAST_SESSION_KEY)
        ) {
          sessionStorage.setItem(COMP_EXT_TOAST_SESSION_KEY, '1');
          const plural = badge === 1 ? '' : 's';
          showNotification(
            `You have ${badge} Comprehensive extension${plural} due. Open Extend Comprehensive to request via email.`,
            'warning',
            9000
          );
        }
      } catch (_) {
        /* non-blocking */
      }
    };

    loadCompExtNotifications();
    const interval = setInterval(loadCompExtNotifications, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const showRenewals = canSeeRenewals(user);

  useEffect(() => {
    if (!showRenewals) {
      setRenewalBadge(0);
      setRenewalPreview([]);
      return undefined;
    }

    let cancelled = false;
    const loadRenewalAlerts = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        const res = await fetch(getApiUrl('/api/admin/renewals/summary'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const badge = res.ok && data.success ? Number(data.summary?.badge) || 0 : 0;
        setRenewalBadge(badge);
        setRenewalPreview(res.ok && data.success && Array.isArray(data.preview) ? data.preview : []);
        if (badge > 0 && !sessionStorage.getItem(RENEWAL_TOAST_SESSION_KEY)) {
          sessionStorage.setItem(RENEWAL_TOAST_SESSION_KEY, '1');
          const plural = badge === 1 ? '' : 's';
          showNotification(
            `You have ${badge} TPO/TOR renewal${plural} inside the 30-day window.`,
            'warning',
            9000
          );
        }
      } catch (_) {
        /* non-blocking */
      }
    };

    loadRenewalAlerts();
    const interval = setInterval(loadRenewalAlerts, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [showRenewals]);

  const updateRenewalBellPos = useCallback(() => {
    const el = renewalBellRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setRenewalBellPos({
      top: Math.round(rect.bottom + 10),
      right: Math.round(Math.max(8, window.innerWidth - rect.right))
    });
  }, []);

  useLayoutEffect(() => {
    if (!renewalBellOpen) return undefined;
    updateRenewalBellPos();
    const onReposition = () => updateRenewalBellPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [renewalBellOpen, updateRenewalBellPos]);

  useEffect(() => {
    if (!renewalBellOpen) return undefined;
    const onDocClick = (event) => {
      const inBtn = renewalBellRef.current && renewalBellRef.current.contains(event.target);
      const inMenu = renewalDropdownRef.current && renewalDropdownRef.current.contains(event.target);
      if (!inBtn && !inMenu) setRenewalBellOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setRenewalBellOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [renewalBellOpen]);

  const updateCompExtBellPos = useCallback(() => {
    const el = compExtBellRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCompExtBellPos({
      top: Math.round(rect.bottom + 10),
      right: Math.round(Math.max(8, window.innerWidth - rect.right))
    });
  }, []);

  useLayoutEffect(() => {
    if (!compExtBellOpen) return undefined;
    updateCompExtBellPos();
    const onReposition = () => updateCompExtBellPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [compExtBellOpen, updateCompExtBellPos]);

  useEffect(() => {
    if (!compExtBellOpen) return undefined;
    const onDocClick = (e) => {
      const inBtn = compExtBellRef.current && compExtBellRef.current.contains(e.target);
      const inMenu = compExtDropdownRef.current && compExtDropdownRef.current.contains(e.target);
      if (!inBtn && !inMenu) setCompExtBellOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setCompExtBellOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [compExtBellOpen]);

  useEffect(() => {
    // Load dashboard data from API
    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('adminToken');
        
        if (!token) {
          showNotification('Please log in again. Session expired.', 'warning');
          // Use default empty data matching API structure
          setDashboardData({
            totalAgents: 0,
            totalCovers: 0,
            totalRevenue: 0,
            monthlyRevenue: 0,
            pendingKYC: 0,
            activePolicies: 0,
            recentPolicies: []
          });
          setIsLoading(false);
          return;
        }

        if (!isFinanceDashboardRole(user)) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setDashboardData(result.data);
          } else {
            showNotification('Dashboard API returned unsuccessful response', 'warning');
            // Use default empty data
            setDashboardData({
              totalPolicies: 0,
              totalRevenue: 0,
              monthlyRevenue: 0,
              activePolicies: 0,
              pendingPolicies: 0,
              expiredPolicies: 0,
              recentPolicies: []
            });
          }
        } else {
          // Handle authentication errors intelligently
          let errorMessage = `Failed to fetch dashboard data (${response.status})`;
          let shouldLogout = false;
          
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
            
            // Check if token is expired or invalid
            if (response.status === 403 && (errorData.code === 'TOKEN_EXPIRED' || errorData.error?.includes('expired'))) {
              errorMessage = 'Your session has expired. Please log in again.';
              shouldLogout = true;
            } else if (response.status === 403 && (errorData.code === 'INVALID_TOKEN' || errorData.error?.includes('Invalid'))) {
              errorMessage = 'Invalid session. Please log in again.';
              shouldLogout = true;
            } else if (response.status === 401) {
              errorMessage = 'Authentication required. Please log in again.';
              shouldLogout = true;
            }
            
            console.error('Dashboard API error:', errorData);
          } catch (e) {
            console.error('Dashboard API error:', response.status, response.statusText);
            if (response.status === 403 || response.status === 401) {
              shouldLogout = true;
            }
          }
          
          // Clear token and redirect to login if authentication failed
          if (shouldLogout) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            // Trigger page reload to show login screen
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
          
          showNotification(errorMessage, shouldLogout ? 'error' : 'warning');
          // Use default empty data matching API structure
          setDashboardData({
            totalAgents: 0,
            totalCovers: 0,
            totalRevenue: 0,
            monthlyRevenue: 0,
            pendingKYC: 0,
            activePolicies: 0,
            recentPolicies: []
          });
        }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
        showNotification('Error loading dashboard data. Please refresh the page.', 'error');
        // Use default empty data matching API structure
        setDashboardData({
          totalAgents: 0,
          totalCovers: 0,
          totalRevenue: 0,
          monthlyRevenue: 0,
          pendingKYC: 0,
          activePolicies: 0,
          recentPolicies: []
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [API_BASE_URL, user?.role]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Close mobile menu when tab changes
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent body scroll when menu is open
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'overview':
        if (isUnderwritingRole(user)) {
          return <UnderwritingOverview user={user} onTabChange={handleTabChange} onShowNotification={showNotification} />;
        }
        return <DashboardOverview onTabChange={handleTabChange} onShowNotification={showNotification} />;
      case 'declarations':
        return <DeclarationsTable user={user} onShowNotification={showNotification} />;
      case 'transactions':
        return <AllTransactionsTable user={user} onShowNotification={showNotification} />;
      case 'agents-analytics':
        return <AgentsAnalytics user={user} onShowNotification={showNotification} />;
      case 'product-rates':
        return <ProductRatesPage user={user} onShowNotification={showNotification} />;
      case 'unsynchronized-policies':
        return <UnsynchronizedPolicies user={user} onShowNotification={showNotification} />;
      case 'reports':
        return <AdminReports user={user} onShowNotification={showNotification} />;
      case 'paid-no-cert':
        return <PaidNoCertAttempt user={user} onShowNotification={showNotification} />;
      case 'kyc':
        return <KYCManagement user={user} onShowNotification={showNotification} />;
      case 'self-registered-agents':
        return <SelfRegisteredAgents user={user} onShowNotification={showNotification} />;
      case 'password-management':
        return <AgentPasswordManagement user={user} onShowNotification={showNotification} />;
      case 'create-from-tq':
        return <CreateAgentsFromTQ user={user} onShowNotification={showNotification} />;
      case 'revenue':
        return <RevenueAnalytics data={dashboardData} onShowNotification={showNotification} />;
      case 'commissions-payable':
        return <CommissionsPayable user={user} onShowNotification={showNotification} />;
      case 'users':
        return <UserManagement user={user} onShowNotification={showNotification} />;
      case 'super-admin-management':
        return <SuperAdminManagement user={user} onShowNotification={showNotification} />;
      case 'settings':
        return <Settings user={user} onLogout={onLogout} onShowNotification={showNotification} />;
      case 'underwriting-issue-cover':
        return <UnderwritingIssueCover user={user} onShowNotification={showNotification} />;
      case 'underwriting-renewals':
        return showRenewals ? (
          <Renewals user={user} />
        ) : (
          <div className="ab-page" style={{ padding: '2rem' }}>
            <p>Renewals are not open on this account yet.</p>
          </div>
        );
      case 'underwriting-extend-policy':
        return <UnderwritingIssueCover user={user} onShowNotification={showNotification} startWithExtension />;
      case 'underwriting-covers-issued':
        return <UnderwritingCoversIssued user={user} onShowNotification={showNotification} />;
      case 'underwriting-comprehensive':
        return (
          <UnderwritingIssueCover
            user={user}
            onShowNotification={showNotification}
            comprehensiveOnly
          />
        );
      case 'underwriting-comp-continuations':
        if (!canSeeComprehensiveUnderwritingNav(user)) {
          return (
            <div className="ab-page" style={{ padding: '2rem' }}>
              <p>Extend Comprehensive is available to underwriting staff only.</p>
            </div>
          );
        }
        return (
          <CompContinuations
            authToken={localStorage.getItem('adminToken')}
            agentProfile={user}
          />
        );
      case 'underwriting-issued-covers-admin':
        return <AdminUnderwritingIssuedCovers user={user} onShowNotification={showNotification} />;
      case 'comprehensive-issued':
        return <AdminComprehensiveIssued user={user} onShowNotification={showNotification} />;
      default:
        if (isUnderwritingRole(user)) {
          return <UnderwritingOverview user={user} onTabChange={handleTabChange} onShowNotification={showNotification} />;
        }
        return <DashboardOverview onTabChange={handleTabChange} onShowNotification={showNotification} />;
    }
  };

  if (isLoading && activeTab === 'overview') {
    return (
      <div className="admin-dashboard">
        <div className="loading-screen">
          <div className="loading-container">
            <div className="loading-logo">🛡️</div>
            <div className="loading-spinner"></div>
            <h2>Loading Dashboard...</h2>
            <p>Validating...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      <Sidebar 
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        user={user}
        onLogout={onLogout}
        onBackToMain={onBackToMain}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
        style={{ display: 'flex', position: 'fixed', left: 0, top: 0, zIndex: 200 }}
      />
      
      <div className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-left">
              {/* Mobile Menu Button */}
              <button 
                className="mobile-menu-btn"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <Menu size={22} strokeWidth={2} />
              </button>
              
              <div className="header-title-section">
                <div className="agency-name-display">
                  <MonarchLogo variant="mark" className="header-monarch-logo" />
                  <span className="agency-text">Monarch Insurance</span>
                </div>
                
                <h1 className="dashboard-title">
                  {activeTab === 'overview' && (isUnderwritingRole(user) ? 'Underwriting Dashboard' : 'Dashboard Overview')}
                  {activeTab === 'declarations' && 'Agent Covers'}
                  {activeTab === 'transactions' && 'All Transactions'}
                  {activeTab === 'agents-analytics' && 'Agents'}
                  {activeTab === 'product-rates' && 'Product Rates'}
                  {activeTab === 'unsynchronized-policies' && 'Unsynchronized Policies'}
                  {activeTab === 'reports' && 'Reports'}
                  {activeTab === 'kyc' && 'KYC Verification'}
                  {activeTab === 'self-registered-agents' && 'Self-Registered Agents'}
                  {activeTab === 'password-management' && 'Agent Password Management'}
                  {activeTab === 'create-from-tq' && 'Create Agents from TQ'}
                  {activeTab === 'revenue' && 'Revenue Analytics'}
                  {activeTab === 'commissions-payable' && 'Commissions Payable'}
                  {activeTab === 'users' && 'User Management'}
                  {activeTab === 'super-admin-management' && 'Super Admin Management'}
                  {activeTab === 'settings' && 'Settings'}
                  {activeTab === 'underwriting-issue-cover' && 'Issue Cover for Agent'}
                  {activeTab === 'underwriting-renewals' && 'Renewals'}
                  {activeTab === 'underwriting-extend-policy' && 'Buy/Extend Policy for Agent'}
                  {activeTab === 'underwriting-covers-issued' && 'Covers Issued'}
                  {activeTab === 'underwriting-comprehensive' && 'Comprehensive'}
                  {activeTab === 'underwriting-comp-continuations' && 'Extend Comprehensive'}
                  {activeTab === 'underwriting-issued-covers-admin' && 'Underwriting Issued Covers'}
                  {activeTab === 'comprehensive-issued' && 'Comprehensive Issued'}
                </h1>
                <p className="dashboard-subtitle">
                  {activeTab === 'overview' && (isUnderwritingRole(user)
                    ? 'Track covers issued, premiums, and certificate status'
                    : 'Monitor your insurance business performance')}
                  {activeTab === 'declarations' && 'View all covers issued by agents with filtering'}
                  {activeTab === 'agents-analytics' && 'Review agent business transactions, commissions, and certificates'}
                  {activeTab === 'product-rates' && 'Manage pricing rates for TPO and TOR covers'}
                  {activeTab === 'unsynchronized-policies' && 'Reconcile issued certificates pending core submission'}
                  {activeTab === 'reports' && 'Export filtered snapshots as CSV or Excel'}
                  {activeTab === 'kyc' && 'Review and verify agent KYC documents and status'}
                  {activeTab === 'self-registered-agents' && 'Sign Up accounts only — review after the IRA licence is uploaded'}
                  {activeTab === 'password-management' && 'Reset passwords for agents individually or all at once'}
                  {activeTab === 'create-from-tq' && 'Query TQ (Oracle) DB and create agent accounts in AgentsBima'}
                  {activeTab === 'revenue' && 'Analyze revenue trends and financial metrics'}
                  {activeTab === 'commissions-payable' && 'Track and manage agent commission payments'}
                  {activeTab === 'users' && 'Manage admin users and permissions'}
                  {activeTab === 'super-admin-management' && 'Manage super admins and finance team members'}
                  {activeTab === 'settings' && 'Configure your admin account and preferences'}
                  {activeTab === 'underwriting-issue-cover' && 'Choose agent or direct business, then complete the purchase-cover flow'}
                  {activeTab === 'underwriting-renewals' && 'Renew TPO and TOR covers on your book. The 30-day rule applies, and each renewal gets a new policy number.'}
                  {activeTab === 'underwriting-extend-policy' && 'Choose agent or direct business, then buy TPO/TOR (direct) or search, buy, or extend for an agent'}
                  {activeTab === 'underwriting-covers-issued' && 'Covers you have issued'}
                  {activeTab === 'underwriting-comprehensive' && 'Same purchase flow as agents — Comprehensive (1 month · 30%)'}
                  {activeTab === 'underwriting-comp-continuations' && 'Email requests@ from your mailbox to extend COMP (not issued in portal)'}
                  {activeTab === 'underwriting-issued-covers-admin' && 'View all covers issued by underwriting users, including issuer and agency'}
                  {activeTab === 'comprehensive-issued' && 'Track COMP certificates — premium, Core posting, extensions due. Use Retry pending Core for failed/skipped posts.'}
                </p>
              </div>
            </div>
            
            <div className="header-right">
              {/* Navigation Bar with Action Buttons */}
              <nav className="header-nav-bar">
                {showRenewals && (
                  <div className="comp-ext-bell-wrap" ref={renewalBellRef}>
                    <button
                      type="button"
                      className={`nav-bar-btn comp-ext-bell-btn${renewalBadge > 0 ? ' has-alerts' : ''}${renewalBellOpen ? ' is-open' : ''}`}
                      title="TPO and TOR renewals"
                      aria-label="Renewal notifications"
                      aria-expanded={renewalBellOpen}
                      onClick={() => {
                        setRenewalBellOpen((open) => {
                          const next = !open;
                          if (next) requestAnimationFrame(() => updateRenewalBellPos());
                          return next;
                        });
                      }}
                    >
                      <span className="btn-icon comp-ext-bell-icon">
                        <Bell size={18} strokeWidth={2.25} />
                        {renewalBadge > 0 && (
                          <span className="comp-ext-bell-badge">
                            {renewalBadge > 99 ? '99+' : renewalBadge}
                          </span>
                        )}
                      </span>
                      <span className="btn-text">Renewals</span>
                    </button>
                    {renewalBellOpen &&
                      createPortal(
                        <div
                          ref={renewalDropdownRef}
                          className="comp-ext-bell-dropdown"
                          role="menu"
                          style={{ top: renewalBellPos.top, right: renewalBellPos.right }}
                        >
                          <div className="comp-ext-bell-head">
                            <div className="comp-ext-bell-head-row">
                              <span className="comp-ext-bell-head-title">Upcoming renewals</span>
                              {renewalBadge > 0 && (
                                <span className="comp-ext-bell-count-chip">{renewalBadge} due</span>
                              )}
                            </div>
                            <p className="comp-ext-bell-head-sub">
                              TPO and TOR covers inside the 30-day window
                            </p>
                          </div>
                          {renewalPreview.length === 0 ? (
                            <div className="comp-ext-bell-empty">
                              <span className="comp-ext-bell-empty-icon" aria-hidden>◎</span>
                              <p>No TPO or TOR renewal is due in the next 30 days.</p>
                            </div>
                          ) : (
                            <ul className="comp-ext-bell-list">
                              {renewalPreview.map((item) => {
                                const days = item.daysLeft;
                                const daysText =
                                  days == null
                                    ? '—'
                                    : days < 0
                                      ? `Expired ${Math.abs(days)}d`
                                      : days === 0
                                        ? 'Today'
                                        : `${days}d left`;
                                return (
                                  <li key={item.clientId}>
                                    <button
                                      type="button"
                                      className="comp-ext-bell-item"
                                      onClick={() => {
                                        setRenewalBellOpen(false);
                                        handleTabChange('underwriting-renewals');
                                      }}
                                    >
                                      <div className="comp-ext-bell-item-top">
                                        <span className="comp-ext-bell-reg">
                                          {item.registrationNumber || 'No reg'}
                                        </span>
                                        <span className={`comp-ext-bell-days urgency-${item.urgency || 'ok'}`}>
                                          {daysText}
                                        </span>
                                      </div>
                                      <span className="comp-ext-bell-insured">
                                        {item.insuredName || 'Insured —'}
                                      </span>
                                      <span className="comp-ext-bell-meta">
                                        {item.coverType || 'TPO'}
                                        {item.policyNumber ? ` · ${item.policyNumber}` : ''}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          <div className="comp-ext-bell-footer-wrap">
                            <button
                              type="button"
                              className="comp-ext-bell-footer"
                              onClick={() => {
                                setRenewalBellOpen(false);
                                handleTabChange('underwriting-renewals');
                              }}
                            >
                              Open Renewals
                            </button>
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>
                )}
                {isUnderwritingRole(user) && (
                  <div className="comp-ext-bell-wrap" ref={compExtBellRef}>
                    <button
                      type="button"
                      className={`nav-bar-btn comp-ext-bell-btn${compExtUrgent && compExtBadge > 0 ? ' has-alerts' : ''}${compExtBellOpen ? ' is-open' : ''}`}
                      title="Comprehensive extensions"
                      aria-label="Comprehensive extension notifications"
                      aria-expanded={compExtBellOpen}
                      onClick={() => {
                        setCompExtBellOpen((o) => {
                          const next = !o;
                          if (next) {
                            // Position before paint so first frame is not clipped/misplaced
                            requestAnimationFrame(() => updateCompExtBellPos());
                          }
                          return next;
                        });
                      }}
                    >
                      <span className="btn-icon comp-ext-bell-icon">
                        <Bell size={18} strokeWidth={2.25} />
                        {compExtBadge > 0 && (
                          <span className="comp-ext-bell-badge">
                            {compExtBadge > 99 ? '99+' : compExtBadge}
                          </span>
                        )}
                      </span>
                      <span className="btn-text">Comprehensive Extensions</span>
                    </button>
                    {compExtBellOpen &&
                      createPortal(
                        <div
                          ref={compExtDropdownRef}
                          className="comp-ext-bell-dropdown"
                          role="menu"
                          style={{ top: compExtBellPos.top, right: compExtBellPos.right }}
                        >
                          <div className="comp-ext-bell-head">
                            <div className="comp-ext-bell-head-row">
                              <span className="comp-ext-bell-head-title">Extend Comprehensive</span>
                              {compExtBadge > 0 && (
                                <span className="comp-ext-bell-count-chip">{compExtBadge} due</span>
                              )}
                            </div>
                            <p className="comp-ext-bell-head-sub">
                              Policies nearing end of period — request via requests@
                            </p>
                          </div>
                          {compExtPreview.length === 0 ? (
                            <div className="comp-ext-bell-empty">
                              <span className="comp-ext-bell-empty-icon" aria-hidden>◎</span>
                              <p>No Comprehensive extensions need action right now.</p>
                            </div>
                          ) : (
                            <ul className="comp-ext-bell-list">
                              {compExtPreview.map((item) => {
                                const days = item.daysLeft;
                                const daysLabel =
                                  days == null
                                    ? '—'
                                    : days < 0
                                      ? `Expired ${Math.abs(days)}d`
                                      : days === 0
                                        ? 'Today'
                                        : `${days}d left`;
                                const urgencyKey = item.urgency?.key || 'ok';
                                return (
                                  <li key={item.clientId}>
                                    <button
                                      type="button"
                                      className="comp-ext-bell-item"
                                      onClick={() => {
                                        setCompExtBellOpen(false);
                                        handleTabChange('underwriting-comp-continuations');
                                      }}
                                    >
                                      <div className="comp-ext-bell-item-top">
                                        <span className="comp-ext-bell-reg">
                                          {item.registrationNumber || 'No reg'}
                                        </span>
                                        <span className={`comp-ext-bell-days urgency-${urgencyKey}`}>
                                          {daysLabel}
                                        </span>
                                      </div>
                                      <span className="comp-ext-bell-insured">
                                        {item.insuredName || 'Insured —'}
                                      </span>
                                      <span className="comp-ext-bell-meta">
                                        {item.urgency?.label || 'Due'}
                                        {item.policyNumber ? ` · ${item.policyNumber}` : ''}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          <div className="comp-ext-bell-footer-wrap">
                            <button
                              type="button"
                              className="comp-ext-bell-footer"
                              onClick={() => {
                                setCompExtBellOpen(false);
                                handleTabChange('underwriting-comp-continuations');
                              }}
                            >
                              Open Extend Comprehensive
                            </button>
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>
                )}
                <a 
                  href="https://wa.me/254738669931?text=hello" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="nav-bar-btn support-btn" 
                  title="Contact Support via WhatsApp"
                >
                  <span className="btn-icon"><MessageCircle size={18} strokeWidth={2} /></span>
                  <span className="btn-text">Support</span>
                </a>
                <button 
                  className="nav-bar-btn refresh-btn" 
                  title="Refresh Data"
                  onClick={() => window.location.reload()}
                >
                  <span className="btn-icon"><RefreshCw size={18} strokeWidth={2} /></span>
                  <span className="btn-text">Refresh</span>
                </button>
                <button 
                  className="nav-bar-btn home-btn" 
                  title="Back to Main App"
                  onClick={onBackToMain}
                >
                  <span className="btn-icon"><Home size={18} strokeWidth={2} /></span>
                  <span className="btn-text">Home</span>
                </button>
              </nav>
              
              <div className="user-info">
                <div className="user-avatar" title={getAdminRoleLabel(staffRole(user))}>
                  {user?.username?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="user-details">
                  <span className="user-name">{user?.username || 'Admin'}</span>
                  <span className="user-role-badge">{getAdminRoleLabel(staffRole(user))}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="dashboard-content">
          {renderActiveComponent()}
        </main>
      </div>
      
      {/* Snackbar Notifications */}
      {showSnackbar && (
        <Snackbar
          key={snackbarNonce}
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={snackbarDuration}
        />
      )}
      <CoreRetryBackground
        onShowNotification={showNotification}
        onOpenList={() => handleTabChange('unsynchronized-policies')}
      />
    </div>
  );
};

export default AdminDashboard;
